import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Plus, Edit, Trash2, Eye, EyeOff, Star, LogOut, Image as ImageIcon, Video, Save, X } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminDashboard = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [user, setUser] = useState(location.state?.user || null);
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showEditor, setShowEditor] = useState(false);
  const [editingPostId, setEditingPostId] = useState(null);
  const hasCheckedAuth = useRef(false);

  // Form state as individual values to avoid nested object issues
  const [formTitle, setFormTitle] = useState('');
  const [formContent, setFormContent] = useState('');
  const [formExcerpt, setFormExcerpt] = useState('');
  const [formFeaturedImage, setFormFeaturedImage] = useState('');
  const [formCategory, setFormCategory] = useState('');
  const [formTags, setFormTags] = useState('');
  const [formIsFeatured, setFormIsFeatured] = useState(false);
  const [formIsPublished, setFormIsPublished] = useState(true);
  const [galleryImages, setGalleryImages] = useState([]);
  const [videoUrls, setVideoUrls] = useState([]);

  const fetchPosts = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/blog/admin/posts`, { withCredentials: true });
      setPosts(res.data.posts);
    } catch (error) {
      console.error('Error fetching posts:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  const checkAuth = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/auth/me`, { withCredentials: true });
      if (!res.data.is_admin) {
        navigate('/admin/login');
        return;
      }
      setUser(res.data);
      fetchPosts();
    } catch (error) {
      navigate('/admin/login');
    }
  }, [navigate, fetchPosts]);

  useEffect(() => {
    if (location.state?.user) {
      setUser(location.state.user);
      fetchPosts();
      return;
    }
    
    if (hasCheckedAuth.current) return;
    hasCheckedAuth.current = true;
    
    checkAuth();
  }, [location.state, fetchPosts, checkAuth]);

  const handleLogout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch (error) {
      console.error('Logout error:', error);
    }
    navigate('/admin/login');
  };

  const resetForm = () => {
    setFormTitle('');
    setFormContent('');
    setFormExcerpt('');
    setFormFeaturedImage('');
    setFormCategory('');
    setFormTags('');
    setFormIsFeatured(false);
    setFormIsPublished(true);
    setGalleryImages([]);
    setVideoUrls([]);
    setEditingPostId(null);
  };

  const handleNewPost = () => {
    resetForm();
    setShowEditor(true);
  };

  const handleEditPost = (post) => {
    setEditingPostId(post.id);
    setFormTitle(post.title);
    setFormContent(post.content);
    setFormExcerpt(post.excerpt || '');
    setFormFeaturedImage(post.featured_image || '');
    setFormCategory(post.category || '');
    setFormTags(post.tags ? post.tags.join(', ') : '');
    setFormIsFeatured(post.is_featured);
    setFormIsPublished(post.is_published);
    setGalleryImages(post.gallery || []);
    setVideoUrls(post.videos || []);
    setShowEditor(true);
  };

  const handleDeletePost = async (postId) => {
    if (!window.confirm('Delete this post?')) return;
    try {
      await axios.delete(`${API}/blog/posts/${postId}`, { withCredentials: true });
      fetchPosts();
    } catch (error) {
      console.error('Error deleting post:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const postData = {
      title: formTitle,
      content: formContent,
      excerpt: formExcerpt,
      featured_image: formFeaturedImage,
      category: formCategory,
      tags: formTags.split(',').map(t => t.trim()).filter(t => t),
      is_featured: formIsFeatured,
      is_published: formIsPublished,
      gallery: galleryImages,
      videos: videoUrls,
      images: []
    };

    try {
      if (editingPostId) {
        await axios.put(`${API}/blog/posts/${editingPostId}`, postData, { withCredentials: true });
      } else {
        await axios.post(`${API}/blog/posts`, postData, { withCredentials: true });
      }
      setShowEditor(false);
      resetForm();
      fetchPosts();
    } catch (error) {
      console.error('Error saving post:', error);
      alert('Error saving post. Please try again.');
    }
  };

  const addGalleryImage = (url) => {
    if (url.trim()) {
      setGalleryImages([...galleryImages, url.trim()]);
    }
  };

  const removeGalleryImage = (index) => {
    setGalleryImages(galleryImages.filter((_, i) => i !== index));
  };

  const addVideoUrl = (url) => {
    if (url.trim()) {
      setVideoUrls([...videoUrls, url.trim()]);
    }
  };

  const removeVideoUrl = (index) => {
    setVideoUrls(videoUrls.filter((_, i) => i !== index));
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  if (loading) {
    return (
      <div className="bg-void-black min-h-screen flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-neon-red border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="bg-void-black min-h-screen">
      <div className="noise-overlay" />
      
      {/* Header */}
      <header className="glass py-4 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 flex items-center justify-between">
          <h1 className="font-syne font-bold text-xl text-white">
            <span className="text-neon-red">Admin</span> Dashboard
          </h1>
          <div className="flex items-center gap-4">
            {user && (
              <div className="flex items-center gap-2">
                {user.picture && <img src={user.picture} alt={user.name} className="w-8 h-8 rounded-full" />}
                <span className="font-manrope text-sm text-neutral-300">{user.name}</span>
              </div>
            )}
            <button onClick={handleLogout} className="flex items-center gap-2 px-4 py-2 text-neutral-400 hover:text-neon-red">
              <LogOut className="w-4 h-4" /> Logout
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        <div className="flex items-center justify-between mb-8">
          <h2 className="font-syne font-bold text-2xl text-white">Blog Posts</h2>
          <button onClick={handleNewPost} className="btn-primary px-6 py-3 rounded font-manrope font-bold text-sm uppercase flex items-center gap-2">
            <Plus className="w-4 h-4" /> New Post
          </button>
        </div>

        {posts.length === 0 ? (
          <div className="glass rounded-lg p-12 text-center">
            <ImageIcon className="w-16 h-16 text-neutral-600 mx-auto mb-4" />
            <p className="font-manrope text-neutral-400 mb-4">No posts yet. Create your first post!</p>
            <button onClick={handleNewPost} className="btn-primary px-6 py-3 rounded font-manrope font-bold text-sm uppercase">Create Post</button>
          </div>
        ) : (
          <div className="space-y-4">
            {posts.map((post) => (
              <motion.div key={post.id} className="glass rounded-lg p-6 flex items-center gap-6" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
                {post.featured_image ? (
                  <img src={post.featured_image} alt={post.title} className="w-24 h-24 object-cover rounded" />
                ) : (
                  <div className="w-24 h-24 bg-white/5 rounded flex items-center justify-center">
                    <ImageIcon className="w-8 h-8 text-neutral-600" />
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    {post.is_featured && <Star className="w-4 h-4 text-yellow-500 fill-yellow-500" />}
                    <h3 className="font-syne font-bold text-lg text-white truncate">{post.title}</h3>
                  </div>
                  <p className="font-manrope text-neutral-400 text-sm truncate">{post.excerpt || post.content.substring(0, 100)}...</p>
                  <div className="flex items-center gap-4 mt-2">
                    <span className="font-manrope text-xs text-neutral-500">{formatDate(post.created_at)}</span>
                    {post.category && <span className="px-2 py-0.5 bg-neon-red/20 text-neon-red text-xs rounded">{post.category}</span>}
                    <span className={`flex items-center gap-1 text-xs ${post.is_published ? 'text-green-500' : 'text-neutral-500'}`}>
                      {post.is_published ? <Eye className="w-3 h-3" /> : <EyeOff className="w-3 h-3" />}
                      {post.is_published ? 'Published' : 'Draft'}
                    </span>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <button onClick={() => handleEditPost(post)} className="p-2 text-neutral-400 hover:text-white"><Edit className="w-5 h-5" /></button>
                  <button onClick={() => handleDeletePost(post.id)} className="p-2 text-neutral-400 hover:text-red-500"><Trash2 className="w-5 h-5" /></button>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </main>

      {/* Editor Modal */}
      {showEditor && (
        <div className="fixed inset-0 z-50 bg-black/80 overflow-y-auto">
          <div className="min-h-screen py-8 px-4">
            <div className="max-w-4xl mx-auto bg-void-black rounded-lg border border-white/10">
              <div className="flex items-center justify-between p-6 border-b border-white/10">
                <h2 className="font-syne font-bold text-xl text-white">{editingPostId ? 'Edit Post' : 'New Post'}</h2>
                <button onClick={() => { setShowEditor(false); resetForm(); }} className="text-neutral-400 hover:text-white"><X className="w-6 h-6" /></button>
              </div>

              <form onSubmit={handleSubmit} className="p-6 space-y-6">
                <div>
                  <label className="block font-manrope text-sm text-neutral-400 mb-2">Title *</label>
                  <input type="text" required value={formTitle} onChange={(e) => setFormTitle(e.target.value)} className="w-full bg-black/50 border border-white/10 rounded px-4 py-3 text-white font-manrope focus:border-neon-red focus:outline-none" placeholder="Post title" />
                </div>

                <div>
                  <label className="block font-manrope text-sm text-neutral-400 mb-2">Content *</label>
                  <textarea required rows={10} value={formContent} onChange={(e) => setFormContent(e.target.value)} className="w-full bg-black/50 border border-white/10 rounded px-4 py-3 text-white font-manrope focus:border-neon-red focus:outline-none resize-none" placeholder="Write your post content..." />
                </div>

                <div>
                  <label className="block font-manrope text-sm text-neutral-400 mb-2">Excerpt</label>
                  <textarea rows={2} value={formExcerpt} onChange={(e) => setFormExcerpt(e.target.value)} className="w-full bg-black/50 border border-white/10 rounded px-4 py-3 text-white font-manrope focus:border-neon-red focus:outline-none resize-none" placeholder="Brief summary" />
                </div>

                <div>
                  <label className="block font-manrope text-sm text-neutral-400 mb-2"><ImageIcon className="w-4 h-4 inline mr-1" />Featured Image URL</label>
                  <input type="url" value={formFeaturedImage} onChange={(e) => setFormFeaturedImage(e.target.value)} className="w-full bg-black/50 border border-white/10 rounded px-4 py-3 text-white font-manrope focus:border-neon-red focus:outline-none" placeholder="https://..." />
                  {formFeaturedImage && <img src={formFeaturedImage} alt="Preview" className="mt-2 h-32 object-cover rounded" />}
                </div>

                <div>
                  <label className="block font-manrope text-sm text-neutral-400 mb-2">Gallery Images</label>
                  <div className="flex gap-2 mb-2">
                    <input type="url" id="gallery-input" className="flex-1 bg-black/50 border border-white/10 rounded px-4 py-2 text-white font-manrope focus:border-neon-red focus:outline-none" placeholder="Image URL" />
                    <button type="button" onClick={() => { const inp = document.getElementById('gallery-input'); addGalleryImage(inp.value); inp.value = ''; }} className="px-4 py-2 bg-white/10 text-white rounded hover:bg-white/20">Add</button>
                  </div>
                  {galleryImages.length > 0 && (
                    <div className="flex flex-wrap gap-2">
                      {galleryImages.map((img, i) => (
                        <div key={i} className="relative">
                          <img src={img} alt="" className="h-20 w-20 object-cover rounded" />
                          <button type="button" onClick={() => removeGalleryImage(i)} className="absolute -top-2 -right-2 bg-red-500 rounded-full p-1"><X className="w-3 h-3" /></button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div>
                  <label className="block font-manrope text-sm text-neutral-400 mb-2"><Video className="w-4 h-4 inline mr-1" />Video URLs</label>
                  <div className="flex gap-2 mb-2">
                    <input type="url" id="video-input" className="flex-1 bg-black/50 border border-white/10 rounded px-4 py-2 text-white font-manrope focus:border-neon-red focus:outline-none" placeholder="Video URL" />
                    <button type="button" onClick={() => { const inp = document.getElementById('video-input'); addVideoUrl(inp.value); inp.value = ''; }} className="px-4 py-2 bg-white/10 text-white rounded hover:bg-white/20">Add</button>
                  </div>
                  {videoUrls.length > 0 && (
                    <div className="space-y-2">
                      {videoUrls.map((vid, i) => (
                        <div key={i} className="flex items-center gap-2 bg-white/5 p-2 rounded">
                          <span className="flex-1 text-sm text-neutral-300 truncate">{vid}</span>
                          <button type="button" onClick={() => removeVideoUrl(i)} className="text-red-500"><X className="w-4 h-4" /></button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block font-manrope text-sm text-neutral-400 mb-2">Category</label>
                    <input type="text" value={formCategory} onChange={(e) => setFormCategory(e.target.value)} className="w-full bg-black/50 border border-white/10 rounded px-4 py-3 text-white font-manrope focus:border-neon-red focus:outline-none" placeholder="e.g., Workflow" />
                  </div>
                  <div>
                    <label className="block font-manrope text-sm text-neutral-400 mb-2">Tags (comma separated)</label>
                    <input type="text" value={formTags} onChange={(e) => setFormTags(e.target.value)} className="w-full bg-black/50 border border-white/10 rounded px-4 py-3 text-white font-manrope focus:border-neon-red focus:outline-none" placeholder="roxxie, ai, video" />
                  </div>
                </div>

                <div className="flex items-center gap-6">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" checked={formIsFeatured} onChange={(e) => setFormIsFeatured(e.target.checked)} className="w-4 h-4 accent-neon-red" />
                    <span className="font-manrope text-sm text-neutral-300">Featured Post</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" checked={formIsPublished} onChange={(e) => setFormIsPublished(e.target.checked)} className="w-4 h-4 accent-neon-red" />
                    <span className="font-manrope text-sm text-neutral-300">Published</span>
                  </label>
                </div>

                <div className="flex items-center gap-4 pt-4 border-t border-white/10">
                  <button type="submit" className="btn-primary px-8 py-3 rounded font-manrope font-bold text-sm uppercase flex items-center gap-2">
                    <Save className="w-4 h-4" /> {editingPostId ? 'Update Post' : 'Create Post'}
                  </button>
                  <button type="button" onClick={() => { setShowEditor(false); resetForm(); }} className="px-8 py-3 border border-white/20 text-white rounded font-manrope font-bold text-sm uppercase hover:border-white/40">Cancel</button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;
